import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ThreeEvent } from '@react-three/fiber';

import { CalculatedLine, Component } from '@paragon/contract-library';
import { distanceLessThanOrEqual, Point2D } from '@paragon/geometry-library';
import { calculateLine } from '@paragon/relative-geometry';
import { RaycastOrder, RenderOrder } from '@paragon/three-shared';
import { assertExhaustiveSwitch, getModifierKeys, notNullish, sortBy } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { groupCoincidentReferenceLines } from '../../library/relativeGeometry/groupCoincidentReferenceLines';
import { pickBlessedCoincidentLine } from '../../library/relativeGeometry/pickBlessedCoincidentLine';
import { onClickInSpaceDuringBasicInsertion } from '../../store/actions/basicInsertionActions';
import { requestContextMenu } from '../../store/actions/contextMenuActions';
import { selectReferenceLine } from '../../store/actions/selectActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import {
  getBasicInsertionInformation,
  isBasicInsertionModeActive,
} from '../../store/selectors/basicInsertionSelectors';
import { getActiveComponent } from '../../store/selectors/getActiveComponentSelector';
import { getCurrentlySelectableItemTypes } from '../../store/selectors/getCurrentlyClickableItemTypes';
import {
  getReferenceLineDisplayMode,
  referenceLinesAreSelectable,
} from '../../store/selectors/getReferenceLineDisplayMode';
import { getReferenceLinesForActiveComponent } from '../../store/selectors/getReferenceLinesForActiveComponent';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';
import { getVisibleReferenceLineGuids } from '../../store/selectors/getVisibleReferenceLineGuids';
import { getPickingObjectInfo } from '../../store/selectors/pickingSelectors';
import { PickingReferenceLineIntersectionInfo } from '../../store/slices/applicationSlice';
import { getCameraClickRadius } from '../ThreeJsSceneActions';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { raycastReferenceLineContainer } from '../util/raycastReferenceLineContainer';
import { BasicInsertionController } from './basic-insertion/BasicInsertionController';
import { PickingReferenceLineIntersectionController } from './PickingReferenceLineIntersectionController';
import { ReferenceLineEditableIntersections } from './ReferenceLineEditableIntersections';
import { ReferenceLineVisual } from './ReferenceLineVisual';

export function ReferenceLineContainer() {
  const component = useAppSelector(getActiveComponent);
  const referenceLines = useAppSelector(getReferenceLinesForActiveComponent);
  const visibleReferenceLineGuids = useAppSelector(getVisibleReferenceLineGuids);

  // Filter out `null` variables so we don't have to do `null` checks in hooks
  return component && referenceLines && visibleReferenceLineGuids ? (
    <ReferenceLineContainerInternal
      component={component}
      referenceLines={referenceLines}
      visibleReferenceLineGuids={visibleReferenceLineGuids}
    />
  ) : null;
}

interface Props {
  readonly component: Component;
  readonly referenceLines: readonly CalculatedLine[];
  readonly visibleReferenceLineGuids: ReadonlySet<string>;
}

function ReferenceLineContainerInternal({ component, referenceLines, visibleReferenceLineGuids }: Props) {
  const dispatch = useAppDispatch();

  const referenceLinesAreInteractive = useAppSelector((state) => {
    const referenceLineDisplayMode = getReferenceLineDisplayMode(state);
    switch (referenceLineDisplayMode.type) {
      case 'none':
        return false;
      case 'picking':
        return true;
      case 'selecting':
        return getCurrentlySelectableItemTypes(state).has('referenceLine');
      default:
        assertExhaustiveSwitch(referenceLineDisplayMode.type);
    }
  });

  const selectedReferenceLineGuid = useAppSelector((state) => {
    const selectedEntry = getSingleSelectedItemEntryOrNull(state);
    return selectedEntry?.type === 'referenceLine' ? selectedEntry.item.guid : null;
  });

  const pickingReferenceLineIntersectionInfo = useAppSelector((state): PickingReferenceLineIntersectionInfo | null => {
    const pickingObjectInfo = getPickingObjectInfo(state);
    return pickingObjectInfo?.type === 'pickingReferenceLineIntersection' ? pickingObjectInfo : null;
  });

  const highlightedLineGuids = Object.keys(useAppSelector((state) => state.viewport.highlightedReferenceLineGuids));

  const basicInsertionInformation = useAppSelector(getBasicInsertionInformation);

  const areReferenceLinesSelectable = useAppSelector(referenceLinesAreSelectable);

  const [hoveredLineGuids, setHoveredLineGuids] = useState<ReadonlySet<string>>(new Set());

  const hoveredLineGuid = useRef<string | null>(null);

  const getClosestLines = useCallback(
    (event: ThreeEvent<MouseEvent>): ClosestLinesResult => {
      if (!areReferenceLinesSelectable) return { closestLine: null, closestLines: [] };
      const worldPosition = Point2D.fromObject(event.point);
      return getClosestLinesInRadiusOfPoint(
        referenceLines,
        visibleReferenceLineGuids,
        component,
        selectedReferenceLineGuid,
        worldPosition,
        getCameraClickRadius(),
      );
    },
    [areReferenceLinesSelectable, component, referenceLines, selectedReferenceLineGuid, visibleReferenceLineGuids],
  );

  const visibleReferenceLines = useMemo(
    () => referenceLines.filter((referenceLine) => visibleReferenceLineGuids.has(referenceLine.guid)),
    [referenceLines, visibleReferenceLineGuids],
  );

  const blessedCoincidentLineGuids = useMemo((): ReadonlySet<string> => {
    const blessingDependencies = { component, selectedReferenceLineGuid };
    const coincidentLineGroups = groupCoincidentReferenceLines(visibleReferenceLines, component);
    return new Set(
      coincidentLineGroups
        .filter((group) => group.length > 1)
        .map((group) => pickBlessedCoincidentLine(group, blessingDependencies).guid),
    );
  }, [component, selectedReferenceLineGuid, visibleReferenceLines]);

  const selectedLine = referenceLines.find(({ guid }) => guid === selectedReferenceLineGuid);

  return (
    <group
      onClick={(event) => {
        if (!threeJsDom.shouldDispatchClickEvent()) return;

        const { closestLine } = getClosestLines(event);
        if (closestLine) {
          event.stopPropagation();
          dispatch(
            selectReferenceLine(closestLine, {
              intent: getModifierKeys(event).ctrlKey ? 'multi-select' : 'select',
              clickPosition: Point2D.fromObject(event.point).toObject(),
            }),
          );
          return;
        }

        // For basic insertion, a click is "in space" as long as it's not over a reference line or intersection.
        // These two cases should have already been handled by now.
        if (isBasicInsertionModeActive(basicInsertionInformation)) {
          // Returns false to represent a handled event
          const result = dispatch(onClickInSpaceDuringBasicInsertion(event.point));
          if (result === false) {
            event.stopPropagation();
          }
        }
      }}
      onContextMenu={async (event) => {
        const { closestLines } = getClosestLines(event);
        const lineGuids = closestLines.map(({ guid }) => guid);
        if (!lineGuids.length) return;
        event.stopPropagation();
        await dispatch(requestContextMenu(event, { type: 'referenceLine', guids: lineGuids }));
      }}
      onPointerMove={(event) => {
        const { closestLine, closestLines } = getClosestLines(event);
        setHoveredLineGuids(new Set(closestLines.map(({ guid }) => guid)));
        if (!closestLine) {
          if (hoveredLineGuid.current) {
            dispatch(clearHoverItemIfActive({ guid: hoveredLineGuid.current, type: 'referenceLine' }));
            hoveredLineGuid.current = null;
          }
          return;
        }
        event.stopPropagation();
        hoveredLineGuid.current = closestLine.guid;
        dispatch(ensureHoverItemIsActive({ guid: closestLine.guid, type: 'referenceLine' }));
      }}
      onPointerOut={() => {
        setHoveredLineGuids(new Set());
        if (hoveredLineGuid.current) {
          dispatch(clearHoverItemIfActive({ guid: hoveredLineGuid.current, type: 'referenceLine' }));
          hoveredLineGuid.current = null;
        }
      }}
      raycast={raycastReferenceLineContainer}
      userData-raycastOrder={RaycastOrder.ReferenceLineContainer}
    >
      {visibleReferenceLines.map((referenceLine) => {
        const selected = referenceLine.guid === selectedReferenceLineGuid;
        const highlighted = highlightedLineGuids.includes(referenceLine.guid);

        const status = highlighted
          ? 'highlighted'
          : selected
            ? 'selected'
            : referenceLinesAreInteractive && hoveredLineGuids.has(referenceLine.guid)
              ? 'hovered'
              : 'normal';

        const renderOrder = blessedCoincidentLineGuids.has(referenceLine.guid)
          ? RenderOrder.BlessedReferenceLineVisual
          : RenderOrder.ReferenceLineVisual;

        return (
          <ReferenceLineVisual
            key={referenceLine.guid}
            referenceLine={referenceLine}
            component={component}
            status={status}
            renderOrder={renderOrder}
          />
        );
      })}
      {isBasicInsertionModeActive(basicInsertionInformation) ? (
        <BasicInsertionController
          component={component}
          visibleReferenceLines={visibleReferenceLines}
          basicLineDefinition={basicInsertionInformation.definition}
          hoveredLineGuids={hoveredLineGuids}
        />
      ) : pickingReferenceLineIntersectionInfo ? (
        <PickingReferenceLineIntersectionController
          component={component}
          visibleReferenceLines={visibleReferenceLines}
          pickingInfo={pickingReferenceLineIntersectionInfo}
        />
      ) : selectedLine ? (
        <ReferenceLineEditableIntersections component={component} selectedLine={selectedLine} />
      ) : null}
    </group>
  );
}

interface ClosestLinesResult {
  readonly closestLine: CalculatedLine | null;
  readonly closestLines: readonly CalculatedLine[];
}

/**
 * Designed to frequently deal with multiple "closest" results due to coincident reference lines. Returns a tuple
 *   containing a) the blessed line (the correct line to use) and b) all the lines that are closest to the test point.
 *
 * Use the first result if you need an unambiguous single result, or the second if you intend to allow the user to
 *   manually disambiguate.
 *
 * Returns `[null, []]` if no lines are within the radius.
 */
function getClosestLinesInRadiusOfPoint(
  referenceLines: readonly CalculatedLine[],
  visibleReferenceLineGuids: ReadonlySet<string>,
  component: Component,
  selectedReferenceLineGuid: string | null,
  testPosition: { x: number; y: number },
  radius: number,
): ClosestLinesResult {
  const testPoint = Point2D.fromObject(testPosition);

  const referenceLinesToUse = referenceLines.filter((line) => visibleReferenceLineGuids.has(line.guid));
  const sortedLinesInRadius = sortBy(
    referenceLinesToUse
      .map((line) => {
        const geometry = calculateLine(line, component);
        return geometry ? { line, distance: testPoint.distanceToLine(geometry) } : null;
      })
      .filter(notNullish)
      .filter((line) => line.distance <= radius),
    (line) => line.distance,
  );

  const closestLineDistance = sortedLinesInRadius[0]?.distance ?? 0;

  const closestLines = sortedLinesInRadius
    .filter((line) => distanceLessThanOrEqual(line.distance, closestLineDistance))
    .map((line) => line.line);

  switch (closestLines.length) {
    case 0:
      return { closestLine: null, closestLines: [] };

    case 1:
      return { closestLine: closestLines[0], closestLines };

    default: {
      const blessedLine = pickBlessedCoincidentLine(closestLines, {
        component,
        selectedReferenceLineGuid,
      });

      return { closestLine: blessedLine, closestLines };
    }
  }
}
